import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { S2T_API_URL, LOGO_SRC } from "@/config";
import { Markdown } from "@/lib/markdown";
import { ArrowLeft, Loader2 } from "lucide-react";

// Route slug -> backend document "kind". Fixed mapping (no guessing);
// identifiers verified against the production /api/legal responses.
const SLUG_TO_KIND = {
  "terms-of-service": "terms_of_service",
  "privacy-policy": "privacy_policy",
  "marketplace-disclaimer": "marketplace_disclaimer",
  "community-guidelines": "community_guidelines",
  "subscription-terms": "subscription_terms",
  "producer-agreement": "producer_agreement",
};

export default function LegalPage() {
  const { slug } = useParams();
  const kind = SLUG_TO_KIND[slug];
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    if (!kind) { setState("notfound"); return; }
    setState("loading");
    fetch(`${S2T_API_URL}/api/legal/${kind}`, { headers: { Accept: "application/json" } })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((d) => {
        setDoc(d);
        setState("ok");
        if (d?.title) document.title = `${d.title} · Source2Table`;
      })
      .catch(() => setState("error"));
  }, [kind]);

  // Strip the body's leading title + italic metadata line (we render our own header).
  const body = doc?.body
    ? doc.body.replace(/^\s*#\s+.*\n+/, "").replace(/^_.*_\s*\n+/, "")
    : "";

  return (
    <div data-testid="legal-page" className="min-h-screen bg-[#F9F6F0]">
      <header className="sticky top-0 z-40 bg-[#F9F6F0]/90 backdrop-blur-md border-b border-[#4A6B41]/15">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-2 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Source2Table logo" className="h-12 w-12 object-contain" />
            <span className="font-sans font-extrabold text-xl sm:text-2xl leading-none tracking-tight">
              <span className="text-black">Source</span>
              <span className="text-[#4A6B41]">2</span>
              <span className="text-black">Table</span>
            </span>
          </Link>
          <Link to="/" data-testid="legal-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2F4829] hover:text-[#4A6B41]">
            <ArrowLeft size={16} /> Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-6 py-10 sm:py-14">
        {state === "loading" && (
          <div data-testid="legal-loading" className="flex items-center gap-2 text-[#5A6757] py-20 justify-center">
            <Loader2 className="animate-spin" size={20} /> Loading document…
          </div>
        )}

        {state === "notfound" && (
          <div data-testid="legal-notfound" className="py-20 text-center">
            <h1 className="font-sans font-extrabold text-2xl text-[#1C241B]">Document not found</h1>
            <p className="mt-3 text-[#5A6757]">We couldn't find that legal document.</p>
            <Link to="/" className="mt-6 inline-block underline text-[#4A6B41] font-semibold">Back to homepage</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="legal-error" className="py-20 text-center">
            <h1 className="font-sans font-extrabold text-2xl text-[#1C241B]">Unable to load document</h1>
            <p className="mt-3 text-[#5A6757]">Please try again in a moment.</p>
            <Link to="/" className="mt-6 inline-block underline text-[#4A6B41] font-semibold">Back to homepage</Link>
          </div>
        )}

        {state === "ok" && doc && (
          <article>
            <div className="pb-6 mb-6 border-b border-[#D8E0D5]">
              <h1 data-testid="legal-title" className="font-sans font-extrabold text-3xl sm:text-4xl text-[#1C241B] tracking-tight">
                {doc.title}
              </h1>
              <p data-testid="legal-meta" className="mt-3 text-sm text-[#5A6757]">
                Version {doc.version} · Effective {doc.effective_date}
              </p>
            </div>
            <div data-testid="legal-body">
              <Markdown text={body} />
            </div>
            <div className="mt-12 pt-6 border-t border-[#D8E0D5]">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A6B41] hover:text-[#2F4829]">
                <ArrowLeft size={16} /> Back to homepage
              </Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
